import React, { useState } from 'react';

import { Container, Document, Header, LanguageButton, LanguageSwitcher, Meta, Section } from './styles';

const publicationDate = '19 de outubro de 2026';
const publicationDateEn = 'October 19, 2026';

const InformationSecurityPolicy: React.FC = () => {
	const [language, setLanguage] = useState<'pt' | 'en'>('pt');
	const isPortuguese = language === 'pt';

	return (
		<Container>
			<Document>
				<LanguageSwitcher aria-label="Language selector">
					<LanguageButton type="button" $active={isPortuguese} onClick={() => setLanguage('pt')}>
						Português
					</LanguageButton>
					<LanguageButton type="button" $active={!isPortuguese} onClick={() => setLanguage('en')}>
						English
					</LanguageButton>
				</LanguageSwitcher>

				{isPortuguese ? (
					<>
						<Header>
							<p>Razio Digital</p>
							<h1>Política de Segurança da Informação</h1>
							<Meta>
								Versão 1.0 | Vigência: {publicationDate} | Revisão: anual e extraordinária
							</Meta>
						</Header>

						<Section>
							<h2>1. Identificação, objetivo e escopo</h2>
							<p>
								Esta Política de Segurança da Informação é mantida por Christian Paes da Silva Sistemas ME,
								CNPJ 13.266.571/0001-14, operando sob a marca Razio Digital. Seu objetivo é preservar a
								confidencialidade, integridade, disponibilidade e rastreabilidade das informações tratadas pela
								organização, inclusive dados e credenciais processados em integrações autorizadas da TikTok Shop.
							</p>
							<p>
								A política se aplica aos colaboradores, prestadores de serviço, sistemas, dispositivos, serviços em
								nuvem e infraestrutura utilizados pela Razio Digital. O responsável pela aprovação e manutenção
								desta política é Christian Paes da Silva.
							</p>
						</Section>

						<Section>
							<h2>2. Governança e responsabilidades</h2>
							<p>
								A direção define e revisa os requisitos de segurança, aprova exceções e assegura recursos para
								cumprimento desta política. Pessoas autorizadas devem proteger credenciais, seguir os controles
								definidos, comunicar incidentes e não compartilhar contas ou acessos.
							</p>
							<p>
								Esta política é revisada ao menos uma vez por ano e sempre que ocorrer incidente relevante,
								alteração material na infraestrutura, mudança legal ou alteração relevante nos serviços.
							</p>
						</Section>

						<Section>
							<h2>3. Controle de acesso</h2>
							<p>
								O acesso é individual, concedido somente a pessoas autorizadas e limitado ao mínimo necessário
								para as atividades desempenhadas. A organização mantém equipe administrativa de duas pessoas,
								com acesso ao servidor por SSH. O login direto da conta root é desativado e o SSH é limitado aos
								IPs fixos autorizados; chaves SSH e senha são exigidas conforme a configuração administrativa.
							</p>
							<p>
								Contas corporativas críticas usam autenticação multifator. Credenciais são individuais, devem ser
								fortes e não podem ser compartilhadas. Acessos são revisados e removidos quando deixam de ser
								necessários.
							</p>
						</Section>

						<Section>
							<h2>4. Segurança de rede, sistemas e dispositivos</h2>
							<p>
								A infraestrutura de produção utiliza firewall gerenciado pela EVEO. As portas públicas são
								limitadas a HTTP (80), HTTPS (443) e SSH (22), sendo o SSH restrito aos IPs fixos da equipe
								autorizada. O banco de dados não é exposto à internet e somente a aplicação e o servidor
								autorizados podem acessá-lo.
							</p>
							<p>
								Serviços públicos utilizam HTTPS com certificado TLS válido. Sistemas operacionais e dependências
								recebem atualizações de segurança mensais. Os dispositivos corporativos utilizam Microsoft
								Defender ou macOS XProtect, bloqueio automático de tela e autenticação local.
							</p>
						</Section>

						<Section>
							<h2>5. Proteção, classificação e retenção de dados</h2>
							<p>
								As informações são classificadas conforme sua sensibilidade e acesso necessário. Dados
								operacionais de pedidos e identificadores de loja ou vendedor são tratados como confidenciais.
								Tokens e credenciais da API TikTok Shop são protegidos por criptografia e acessíveis somente pelo
								serviço autorizado.
							</p>
							<p>
								Os dados em repouso no SQL Server são protegidos por Transparent Data Encryption (TDE). Dados em
								trânsito usam HTTPS/TLS. Dados operacionais de pedidos e identificadores de loja ou vendedor são
								mantidos por até 30 dias e, após esse período, são excluídos ou anonimizados quando aplicável.
							</p>
						</Section>

						<Section>
							<h2>6. Backup, monitoramento e vulnerabilidades</h2>
							<p>
								Backups diários de dados e arquivos essenciais são mantidos em serviço Azure, separados do
								servidor de produção, com criptografia e acesso restrito. Logs do servidor e da aplicação,
								painel da EVEO e alertas por e-mail são usados para acompanhar disponibilidade, erros e eventos
								relevantes de segurança.
							</p>
							<p>
								Vulnerabilidades e ameaças são acompanhadas por avisos de fornecedores, atualizações, análise de
								logs e monitoramento. A equipe prioriza a correção conforme criticidade, registra as ações e
								valida a aplicação das correções.
							</p>
						</Section>

						<Section>
							<h2>7. Gestão de incidentes</h2>
							<p>
								Suspeitas ou incidentes de segurança devem ser comunicados imediatamente pelos canais de e-mail
								e WhatsApp corporativo. O registro formal é mantido por e-mail. A resposta inclui identificação,
								contenção, análise de logs e impacto, erradicação, recuperação, comunicação necessária e registro
								das ações e lições aprendidas.
							</p>
							<p>
								Quando aplicável, a organização avaliará suas obrigações de comunicação a titulares, clientes,
								autoridades e parceiros, incluindo os prazos legais e contratuais aplicáveis.
							</p>
						</Section>

						<Section>
							<h2>8. Conformidade e contato</h2>
							<p>
								O descumprimento desta política pode resultar em revogação de acesso e outras medidas cabíveis.
								Dúvidas, solicitações ou relatos relacionados à segurança devem ser encaminhados para
								<a href="mailto:development@razio.com.br">development@razio.com.br</a>.
							</p>
						</Section>
					</>
				) : (
					<>
						<Header>
							<p>Razio Digital</p>
							<h1>Information Security Policy</h1>
							<Meta>Version 1.0 | Effective date: {publicationDateEn} | Review: annual and event-driven</Meta>
						</Header>

						<Section>
							<h2>1. Identity, purpose and scope</h2>
							<p>
								This Information Security Policy is maintained by Christian Paes da Silva Sistemas ME, CNPJ
								13.266.571/0001-14, operating under the Razio Digital brand. Its purpose is to preserve the
								confidentiality, integrity, availability and traceability of information processed by the
								organization, including data and credentials processed through authorized TikTok Shop integrations.
							</p>
							<p>
								This policy applies to employees, service providers, systems, devices, cloud services and
								infrastructure used by Razio Digital. Christian Paes da Silva is responsible for approving and
								maintaining this policy.
							</p>
						</Section>

						<Section>
							<h2>2. Governance and responsibilities</h2>
							<p>
								Management defines and reviews security requirements, approves exceptions and provides resources
								for compliance with this policy. Authorized personnel must protect credentials, follow defined
								controls, report incidents and must not share accounts or access.
							</p>
							<p>
								This policy is reviewed at least annually and whenever a relevant incident, material
								infrastructure change, legal change or material service change occurs.
							</p>
						</Section>

						<Section>
							<h2>3. Access control</h2>
							<p>
								Access is individual, granted only to authorized personnel and limited to what is necessary for
								their duties. The organization has a two-person administrative team with server access through SSH.
								Direct root login is disabled and SSH is restricted to authorized fixed IP addresses; SSH keys and
								passwords are required according to the administrative configuration.
							</p>
							<p>
								Critical corporate accounts use multi-factor authentication. Credentials are individual, must be
								strong and must not be shared. Access is reviewed and removed when no longer needed.
							</p>
						</Section>

						<Section>
							<h2>4. Network, system and endpoint security</h2>
							<p>
								Production infrastructure uses an EVEO-managed firewall. Public ports are limited to HTTP (80),
								HTTPS (443) and SSH (22), with SSH restricted to the authorized team's fixed IP addresses. The
								database is not exposed to the internet and may only be accessed by the authorized application and
								server.
							</p>
							<p>
								Public services use HTTPS with a valid TLS certificate. Operating systems and dependencies receive
								monthly security updates. Corporate endpoints use Microsoft Defender or macOS XProtect, automatic
								screen locking and local authentication.
							</p>
						</Section>

						<Section>
							<h2>5. Data protection, classification and retention</h2>
							<p>
								Information is classified according to its sensitivity and required access. Operational order data
								and store or seller identifiers are treated as confidential. TikTok Shop API tokens and credentials
								are encrypted and available only to the authorized service.
							</p>
							<p>
								Data at rest in SQL Server is protected by Transparent Data Encryption (TDE). Data in transit uses
								HTTPS/TLS. Operational order data and store or seller identifiers are retained for up to 30 days,
								then deleted or anonymized where applicable.
							</p>
						</Section>

						<Section>
							<h2>6. Backup, monitoring and vulnerability management</h2>
							<p>
								Daily backups of essential data and files are maintained in Azure, separate from the production
								server, with encryption and restricted access. Server and application logs, the EVEO dashboard and
								email alerts are used to monitor availability, errors and relevant security events.
							</p>
							<p>
								Vulnerabilities and threats are monitored through vendor notices, updates, log analysis and
								monitoring. The team prioritizes remediation by severity, records actions and validates fixes.
							</p>
						</Section>

						<Section>
							<h2>7. Incident management</h2>
							<p>
								Suspected or confirmed security incidents must be immediately reported through corporate email and
								WhatsApp. Email is used as the formal record. The response includes identification, containment,
								log and impact analysis, eradication, recovery, required communications, action records and
								lessons learned.
							</p>
							<p>
								When applicable, the organization assesses notification obligations to data subjects, customers,
								authorities and partners, including applicable legal and contractual deadlines.
							</p>
						</Section>

						<Section>
							<h2>8. Compliance and contact</h2>
							<p>
								Failure to comply with this policy may result in access revocation and other applicable measures.
								Questions, requests or reports related to security should be sent to
								<a href="mailto:development@razio.com.br">development@razio.com.br</a>.
							</p>
						</Section>
					</>
				)}

				<p>
					<a href="/documents/politica-de-seguranca-da-informacao-razio-digital.pdf" download>
						{isPortuguese ? 'Baixar a versão em PDF' : 'Download the PDF version'}
					</a>
				</p>
			</Document>
		</Container>
	);
};

export default InformationSecurityPolicy;
